import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { unAuthenticatedApiService } from 'services/service';
import { ApiEndpoints } from 'services/endpoints';

/* ─────────────────────────────────────────────────────────────
   useVendors.js — CRA-friendly port of the Next.js useVendors.ts
   Mirrors:
   - useGetAllVendors  →  list + pagination + search
   - useGetVendorById  →  single vendor detail
   No react-query or lodash dependencies; uses native React hooks.
   ───────────────────────────────────────────────────────────── */

/* Strip nil / undefined values from an object before sending as query params.
   Replacement for lodash's omitBy(obj, isNil). */
const stripNil = (obj) => {
    const result = {};
    Object.keys(obj || {}).forEach((key) => {
        const value = obj[key];
        if (value !== null && value !== undefined && value !== '') {
            result[key] = value;
        }
    });
    return result;
};

/* ─────────────────────────────────────────────────────────────
   useVendors — list with pagination and search
   - per_page: page size (default 20)
   - page:     0-indexed page number (default 0, Zoho uses 0-indexed)
   - search:   contact_name query for partial match
   - extras:   any other params to forward as filters
   - debounce: ms to debounce search input (default 500)
   Returns: { vendors, isLoading, error, hasMore, totalCount, refetch }
   ───────────────────────────────────────────────────────────── */
export const useVendors = ({
    per_page = 20,
    page = 0,
    search = null,
    extras = {},
    debounce = 500,
} = {}) => {
    const [vendors, setVendors]       = useState([]);
    const [isLoading, setIsLoading]   = useState(true);
    const [error, setError]           = useState(null);
    const [hasMore, setHasMore]       = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    /* Debounce the search input so we don't fire a request per keystroke */
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    useEffect(() => {
        if (debounce <= 0) {
            setDebouncedSearch(search);
            return;
        }
        const handle = setTimeout(() => setDebouncedSearch(search), debounce);
        return () => clearTimeout(handle);
    }, [search, debounce]);

    /* Stable reference for `extras` so the effect doesn't re-fire on every render
       just because the parent passed a new object literal. */
    const extrasKey = useMemo(() => JSON.stringify(extras || {}), [extras]);

    /* AbortController so a slow in-flight request can't overwrite a newer response */
    const abortRef = useRef(null);

    const fetchVendors = useCallback(async () => {
        /* Cancel any in-flight request from a previous render */
        if (abortRef.current) {
            abortRef.current.abort();
        }
        const controller = new AbortController();
        abortRef.current = controller;

        setIsLoading(true);
        setError(null);

        try {
            const params = stripNil({
                page,
                per_page,
                contact_name: debouncedSearch,
                contact_type: 'vendor',
                cf_user_type: 'B2B',
                ...JSON.parse(extrasKey),
            });

            const { data } = await unAuthenticatedApiService.get(
                ApiEndpoints.GET_CUSTOMERS,
                {
                    params,
                    signal: controller.signal,
                },
            );

            /* If the request was cancelled mid-flight, bail without state updates */
            if (controller.signal.aborted) return;

            /* Backend wraps responses as { status, data: { contacts, page_context } }.
               Try wrapped (current backend) and unwrapped (defensive). */
            const payload = data?.data ?? data;
            const contacts = Array.isArray(payload?.contacts) ? payload.contacts : [];
            const ctx = payload?.page_context ?? {};

            setVendors(contacts);
            setHasMore(Boolean(ctx?.has_more_page));
            setTotalCount(ctx?.total ?? contacts.length);
        } catch (err) {
            /* Ignore abort errors — they're expected when a new request fires */
            if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
            setError(err);
            setVendors([]);
        } finally {
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [page, per_page, debouncedSearch, extrasKey]);

    useEffect(() => {
        fetchVendors();
        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
    }, [fetchVendors]);

    return {
        vendors,
        isLoading,
        error,
        hasMore,
        totalCount,
        refetch: fetchVendors,
    };
};

/* ─────────────────────────────────────────────────────────────
   useVendorById — fetch single vendor by contact_id
   Returns: { vendor, isLoading, error, refetch }
   ───────────────────────────────────────────────────────────── */
export const useVendorById = (id) => {
    const [vendor, setVendor]       = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError]         = useState(null);

    const fetchVendor = useCallback(async () => {
        if (!id) {
            setVendor(null);
            return;
        }
        setIsLoading(true);
        setError(null);

        try {
            const { data } = await unAuthenticatedApiService.get(
                ApiEndpoints.GET_CUSTOMER_DETAILS,
                { params: { contact_id: id } },
            );
            /* Backend wraps responses as { status, data: { contact: {...} } }.
               Try both shapes — wrapped (current backend) and unwrapped (defensive). */
            const payload = data?.data ?? data;
            setVendor(payload?.contact ?? null);
        } catch (err) {
            setError(err);
            setVendor(null);
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchVendor();
    }, [fetchVendor]);

    return {
        vendor,
        isLoading,
        error,
        refetch: fetchVendor,
    };
};

/* ─────────────────────────────────────────────────────────────
   useVendorByName — look up a vendor's full record by their NAME.
   Used when product data has vendor_name but not vendor_id, and we
   need to link from a product detail page to the vendor page.

   Session-cached so navigating between products of the same vendor
   doesn't refetch. Cache lives only for this page session — refresh
   the browser and it's gone.

   Returns: { vendor, isLoading, error }
   ───────────────────────────────────────────────────────────── */

/* Module-scoped cache. Lives for the session, not persisted. */
const _vendorByNameCache = new Map();
const _vendorByNameInflight = new Map();

const norm = (s) => String(s ?? '').trim().toLowerCase();

export const useVendorByName = (name) => {
    const [vendor, setVendor]       = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError]         = useState(null);

    useEffect(() => {
        const key = norm(name);
        if (!key) {
            setVendor(null);
            setIsLoading(false);
            setError(null);
            return;
        }

        /* Cache hit — instant return, no fetch */
        if (_vendorByNameCache.has(key)) {
            setVendor(_vendorByNameCache.get(key));
            setIsLoading(false);
            setError(null);
            return;
        }

        /* Already-in-flight request for this name — piggyback on it */
        if (_vendorByNameInflight.has(key)) {
            setIsLoading(true);
            _vendorByNameInflight.get(key)
                .then((result) => {
                    setVendor(result);
                    setIsLoading(false);
                })
                .catch((err) => {
                    setError(err);
                    setIsLoading(false);
                });
            return;
        }

        /* Fresh fetch */
        setIsLoading(true);
        setError(null);

        const promise = (async () => {
            try {
                const { data } = await unAuthenticatedApiService.get(
                    ApiEndpoints.GET_CUSTOMERS,
                    {
                        params: stripNil({
                            contact_name: name,
                            contact_type: 'vendor',
                            cf_user_type: 'B2B',
                            per_page: 5,
                            page: 1,
                        }),
                    },
                );

                /* Backend wraps responses as { status, data: { contacts } }.
                   Unwrap and then narrow to exact case-insensitive match. */
                const payload = data?.data ?? data;
                const contacts = Array.isArray(payload?.contacts) ? payload.contacts : [];
                const target = norm(name);
                const exact = contacts.find(
                    (c) => norm(c?.vendor_name) === target || norm(c?.contact_name) === target
                );
                /* Fall back to first result if no exact match (might be slight name variation) */
                const found = exact ?? contacts[0] ?? null;

                _vendorByNameCache.set(key, found);
                return found;
            } finally {
                _vendorByNameInflight.delete(key);
            }
        })();

        _vendorByNameInflight.set(key, promise);

        promise
            .then((result) => {
                setVendor(result);
                setIsLoading(false);
            })
            .catch((err) => {
                setError(err);
                setVendor(null);
                setIsLoading(false);
            });
    }, [name]);

    return { vendor, isLoading, error };
};

export const getVendorId = (v) =>
    v?.contact_id ?? v?.id ?? null;

/* Display name — works for BOTH list (vendor_name) and detail (contact_name) responses */
export const getVendorName = (v) =>
    v?.vendor_name ?? v?.contact_name ?? v?.company_name ?? '';

/* Image — backend stores as JSON-encoded array string. Parse and take first. */
export const getVendorImage = (v) => {
    const raw = v?.cf_vendor_profile_image;
    if (!raw) {
        /* Fallbacks for other possible image fields */
        return v?.image_url ?? v?.logo ?? v?.profile_image ?? null;
    }
    /* Already an array? */
    if (Array.isArray(raw)) return raw[0] ?? null;
    /* JSON-encoded string of array */
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed[0] ?? null;
        return typeof parsed === 'string' ? parsed : null;
    } catch {
        /* Plain string URL, not JSON */
        return typeof raw === 'string' ? raw : null;
    }
};

/* Verified — backend custom field uses "Yes" / "No" strings */
export const getVendorVerified = (v) =>
    v?.cf_verified === 'Yes'
    || Boolean(v?.is_verified)
    || Boolean(v?.verified);

/* Description / about text */
export const getVendorDescription = (v) =>
    v?.notes ?? v?.description ?? '';

/* Address — confirmed field is cf_vendor_address (free-text) */
export const getVendorAddress = (v) =>
    v?.cf_vendor_address ?? '';

/* Structured location — falls back to billing/shipping address if cf_vendor_address absent */
export const getVendorLocation = (v) => {
    if (v?.cf_vendor_address) return v.cf_vendor_address;
    const billing = v?.billing_address ?? {};
    const shipping = v?.shipping_address ?? {};
    const city = billing.city || shipping.city || '';
    const state = billing.state || shipping.state || '';
    const country = billing.country || shipping.country || '';
    return [city, state, country].filter(Boolean).join(', ');
};

/* MOQ (minimum order value in raw NGN) — confirmed: cf_vendor_moq */
export const getVendorMoq = (v) =>
    Number(v?.cf_vendor_moq ?? 0);

/* Product count — still a guess, backend may not include this in list response */
export const getVendorProductCount = (v) =>
    v?.products_count
    ?? v?.product_count
    ?? v?.custom_field_hash?.cf_product_count
    ?? 0;

/* ─────────────────────────────────────────────────────────────
   Helpers
   ───────────────────────────────────────────────────────────── */

/* Build the canonical vendor URL — single source of truth.
   Use this instead of hardcoding /dashboard/vendors/${...} anywhere. */
export const buildVendorUrl = (vendor) => {
    const id = getVendorId(vendor);
    return id ? `/dashboard/vendors/${id}` : '/dashboard/vendors';
};

const TEST_NAME_PATTERNS = /\b(test|dummy|demo|xxxx|sample)\b/i;

export const isDisplayableVendor = (v) => {
    if (!v) return false;
    const name = getVendorName(v);
    if (!name || !name.trim()) return false;
    if (TEST_NAME_PATTERNS.test(name)) return false;
    if (!getVendorVerified(v)) return false;
    return true;
};

/* Convert raw vendor for use in UI cards — gives a normalized object
   so components don't all duplicate field access. */
export const normalizeVendor = (v) => ({
    id:           getVendorId(v),
    name:         getVendorName(v),
    image:        getVendorImage(v),
    verified:     getVendorVerified(v),
    description:  getVendorDescription(v),
    address:      getVendorAddress(v),
    location:     getVendorLocation(v),
    moq:          getVendorMoq(v),
    productCount: getVendorProductCount(v),
    url:          buildVendorUrl(v),
    raw:          v,
});

/* ─────────────────────────────────────────────────────────────
   Default export — back-compat for existing imports
   that may have used `import useVendors from 'hooks/useVendors'`
   ───────────────────────────────────────────────────────────── */
export default useVendors;